import type { UseFormReturn } from 'react-hook-form';
import { TextField } from '@/components/forms/TextField';
import { SelectField } from '@/components/forms/SelectField';
import type { CreateStartupFormValues } from '../schema';
import {
  CREATE_STARTUP_STRINGS,
  FIELD_LABELS,
  FIELD_PLACEHOLDERS,
  PAST_EXITS_OPTIONS,
} from '../constants';

interface CoFounderCardProps {
  form: UseFormReturn<CreateStartupFormValues>;
  index: number;
  /** The first co-founder is always present and cannot be removed. */
  onRemove?: () => void;
}

/** One co-founder inset card on Step 2 (Figma 196:656). */
export function CoFounderCard({ form, index, onRemove }: CoFounderCardProps) {
  const {
    register,
    formState: { errors },
  } = form;
  const memberErrors = errors.foundingTeam?.[index];

  return (
    <div className="flex flex-col gap-5 rounded-card border border-input-border bg-page-bg/60 p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-sans font-semibold text-navy">
          {CREATE_STARTUP_STRINGS.MEMBER_TITLE_PREFIX}
          {index + 1}
        </h3>
        {/* Raw button (mnr-standards narrow exception): red destructive text
            link per Figma; no Button variant covers it without class conflicts. */}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="font-sans text-sm font-medium text-red hover:text-red/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red"
            aria-label={`${CREATE_STARTUP_STRINGS.REMOVE} co-founder ${index + 1}`}
          >
            {CREATE_STARTUP_STRINGS.REMOVE}
          </button>
        )}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <TextField
          {...register(`foundingTeam.${index}.name`)}
          label={FIELD_LABELS.memberName}
          labelVariant="caps"
          placeholder={FIELD_PLACEHOLDERS.memberName}
          error={memberErrors?.name?.message}
        />
        <TextField
          {...register(`foundingTeam.${index}.title`)}
          label={FIELD_LABELS.memberTitle}
          labelVariant="caps"
          placeholder={FIELD_PLACEHOLDERS.memberTitle}
          error={memberErrors?.title?.message}
        />
        <TextField
          {...register(`foundingTeam.${index}.linkedinUrl`)}
          label={FIELD_LABELS.memberLinkedin}
          labelVariant="caps"
          type="url"
          placeholder={FIELD_PLACEHOLDERS.memberLinkedin}
          error={memberErrors?.linkedinUrl?.message}
        />
        <SelectField
          {...register(`foundingTeam.${index}.pastExits`)}
          label={FIELD_LABELS.memberPastExits}
          labelVariant="caps"
          placeholder="Select"
          options={PAST_EXITS_OPTIONS}
          error={memberErrors?.pastExits?.message}
        />
      </div>
    </div>
  );
}
